import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { fmtL, apiErr } from "@/lib/format";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { MSG_GUARDAR } from "@/lib/modalForm";
import { TagIcon, MagnifyingGlassIcon, WarningIcon, ArrowRightIcon, CheckSquareIcon, SquareIcon } from "@phosphor-icons/react";

const NIVELES = ["p0", "p1", "p2", "p3", "p4"];
const POR_PAGINA = 50;
const COLOR = {
  p0: "text-agro-yellow border-agro-yellow/50 bg-agro-yellow/10",
  p1: "text-agro-green border-agro-green/50 bg-agro-green/10",
  p2: "text-blue-300 border-blue-400/50 bg-blue-400/10",
  p3: "text-purple-300 border-purple-400/50 bg-purple-400/10",
  p4: "text-orange-300 border-orange-400/50 bg-orange-400/10",
};

/** Mismo criterio que el servidor: nivel del producto > «Todos los demás» (o nivel del cliente) > primer nivel con precio. */
function efectivo(p, explicito, base) {
  for (const c of [explicito, base]) {
    if (c && Number(p[`precio_${c}`]) > 0) return { nivel: c, precio: Number(p[`precio_${c}`]), atencion: false };
  }
  for (const c of ["p1", "p2", "p3", "p4"]) {
    if (Number(p[`precio_${c}`]) > 0) return { nivel: c, precio: Number(p[`precio_${c}`]), atencion: Boolean(explicito || base) };
  }
  return { nivel: explicito || base || "p4", precio: 0, atencion: true };
}

/**
 * Precios por producto del cliente: cada producto puede asignarse EXPLÍCITAMENTE a un nivel (P0–P4) y un solo nivel
 * puede marcarse como «Todos los demás» (los productos sin asignación explícita, incluidos los que se creen después).
 * Las selecciones se conservan al buscar o cambiar de página y se guardan juntas.
 */
export default function PricingTab({ cid, client }) {
  const { user } = useAuth();
  // Asignar tarifas por producto: solo el Administrador General (los demás roles consultan)
  const puedeEditar = user?.role === "admin" || Boolean(user?.effective?.is_admin);
  const [productos, setProductos] = useState([]);
  const [asig, setAsig] = useState({});            // producto_id → nivel explícito
  const [resto, setResto] = useState(null);         // nivel de «Todos los demás»
  const [orig, setOrig] = useState({ asig: {}, resto: null });
  const [nivelCliente, setNivelCliente] = useState("");
  const [activo, setActivo] = useState("p2");       // nivel que se está editando
  const [q, setQ] = useState("");
  const [filtro, setFiltro] = useState("todos");   // todos | nivel | automaticos | atencion
  const [pag, setPag] = useState(1);
  const [cargando, setCargando] = useState(true);
  const [guardando, setGuardando] = useState(false);

  const cargar = async () => {
    setCargando(true);
    try {
      const [{ data: pl }, { data: prods }] = await Promise.all([api.get(`/clients/${cid}/price-levels`), api.get("/products")]);
      setProductos((prods || []).filter((p) => p.activo !== false));
      setAsig(pl.asignaciones || {}); setResto(pl.resto || null); setNivelCliente(pl.nivel_cliente || "");
      setOrig({ asig: pl.asignaciones || {}, resto: pl.resto || null });
    } catch (e) { toast.error(apiErr(e)); }
    finally { setCargando(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { cargar(); }, [cid]);

  const base = resto || nivelCliente || "p4";     // nivel de los productos sin asignación explícita
  const filas = useMemo(() => productos.map((p) => {
    const ex = asig[p.id] || null;
    return { p, ex, ef: efectivo(p, ex, base) };
  }), [productos, asig, base]);

  const conteo = useMemo(() => {
    const c = Object.fromEntries(NIVELES.map((n) => [n, { ex: 0, auto: 0 }]));
    for (const f of filas) {
      if (f.ex) c[f.ex].ex += 1;
      else if (c[base]) c[base].auto += 1;
    }
    return c;
  }, [filas, base]);
  const atencion = filas.filter((f) => f.ef.atencion);

  const visibles = useMemo(() => {
    const s = q.trim().toLowerCase();
    return filas.filter((f) => {
      if (s && !`${f.p.codigo} ${f.p.sku || ""} ${f.p.nombre || ""}`.toLowerCase().includes(s)) return false;
      if (filtro === "nivel") return f.ex === activo;
      if (filtro === "automaticos") return !f.ex;
      if (filtro === "atencion") return f.ef.atencion;
      return true;
    });
  }, [filas, q, filtro, activo]);
  const paginas = Math.max(1, Math.ceil(visibles.length / POR_PAGINA));
  const pagina = visibles.slice((Math.min(pag, paginas) - 1) * POR_PAGINA, Math.min(pag, paginas) * POR_PAGINA);

  const cambios = useMemo(() => {
    const ids = new Set([...Object.keys(orig.asig), ...Object.keys(asig)]);
    let n = 0;
    ids.forEach((id) => { if ((orig.asig[id] || null) !== (asig[id] || null)) n += 1; });
    return n + ((orig.resto || null) !== (resto || null) ? 1 : 0);
  }, [asig, resto, orig]);

  // Un producto en UN solo nivel explícito: marcar en el nivel activo; desmarcar lo devuelve a «Todos los demás»
  const alternar = (id) => setAsig((a) => {
    const n = { ...a };
    if (n[id] === activo) delete n[id]; else n[id] = activo;
    return n;
  });
  const marcarVisibles = (marcar) => setAsig((a) => {
    const n = { ...a };
    for (const f of visibles) {
      if (marcar && !f.ex) n[f.p.id] = activo;                 // no mueve los que ya están explícitos en otro nivel
      if (!marcar && n[f.p.id] === activo) delete n[f.p.id];
    }
    return n;
  });
  const alternarResto = (nivel) => setResto((r) => (r === nivel ? null : nivel));

  const guardar = async () => {
    if (!cambios) return;
    if (!window.confirm(MSG_GUARDAR)) return;
    setGuardando(true);
    try {
      const { data } = await api.put(`/clients/${cid}/price-levels`, { asignaciones: asig, resto });
      toast.success(`Niveles de precio guardados · ${data.explicitas} asignación(es) explícita(s)${data.resto ? ` · Todos los demás: ${data.resto.toUpperCase()}` : ""}`);
      await cargar();
    } catch (e) { toast.error(apiErr(e)); }
    finally { setGuardando(false); }
  };

  if (cargando) return <div className="p-8 text-center text-text-muted">Cargando…</div>;
  const todosVisiblesMarcados = visibles.length > 0 && visibles.every((f) => f.ex === activo);
  return (
    <div className="space-y-4" data-testid="pricing-tab">
      <div className="bg-agro-yellow/5 border border-agro-yellow/30 rounded-lg p-3 text-xs text-text-secondary flex items-start gap-2">
        <TagIcon size={16} className="text-agro-yellow mt-0.5 flex-shrink-0" />
        <div>
          <div className="text-text-primary font-medium mb-0.5">Niveles de precio de {client?.nombre || "este cliente"}</div>
          Selecciona los productos de cada nivel y marca <b>«Todos los demás»</b> en un nivel para cubrir el resto del catálogo
          (también los productos nuevos). Regla: nivel asignado al producto &gt; «Todos los demás» &gt; nivel general del cliente
          ({(nivelCliente || "—").toUpperCase()}). Si el nivel no tiene precio se usa el primer nivel con precio (se marca para revisión).
          {!puedeEditar && <div className="mt-1 text-agro-yellow" data-testid="solo-admin-tarifas">Solo el Administrador General asigna tarifas. Esta vista es de consulta.</div>}
        </div>
      </div>

      {/* Niveles: selección, contador y «Todos los demás» */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2" data-testid="niveles">
        {NIVELES.map((n) => (
          <div key={n} onClick={() => { setActivo(n); setPag(1); }} data-testid={`nivel-${n}`}
            className={`cursor-pointer rounded-lg border p-3 transition-colors ${activo === n ? COLOR[n] + " ring-1 ring-current" : "border-border bg-card hover:border-border/80"}`}>
            <div className="flex items-center justify-between">
              <span className="font-mono font-bold text-lg">{n.toUpperCase()}</span>
              {resto === n && <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-white/10" data-testid={`resto-activo-${n}`}>Todos los demás</span>}
            </div>
            <div className="text-xs mt-1 text-text-secondary" data-testid={`conteo-${n}`}>
              <b className="text-text-primary">{conteo[n].ex}</b> seleccionado{conteo[n].ex !== 1 ? "s" : ""}
              {conteo[n].auto > 0 && <> · <b className="text-text-primary">{conteo[n].auto}</b> automático{conteo[n].auto !== 1 ? "s" : ""}</>}
            </div>
            {puedeEditar && (
              <label className="mt-2 flex items-center gap-1.5 text-[11px] text-text-secondary cursor-pointer" onClick={(e) => e.stopPropagation()}>
                <input type="checkbox" checked={resto === n} onChange={() => alternarResto(n)} className="accent-agro-green" data-testid={`resto-${n}`} />
                Todos los demás
              </label>
            )}
          </div>
        ))}
      </div>
      {!resto && (
        <div className="text-[11px] text-text-muted">
          Sin «Todos los demás»: los productos no seleccionados usan el nivel general del cliente ({(nivelCliente || "p4").toUpperCase()}).
        </div>
      )}

      {/* Búsqueda y filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[240px]">
          <MagnifyingGlassIcon size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPag(1); }} placeholder="Buscar por código, SKU o nombre…"
            data-testid="pricing-search" className="w-full bg-app border border-border rounded-md pl-9 pr-3 py-2 text-sm focus:border-agro-green" />
        </div>
        {[["todos", "Todos"], ["nivel", `Seleccionados en ${activo.toUpperCase()}`], ["automaticos", "Automáticos"], ["atencion", `Requieren atención (${atencion.length})`]].map(([k, l]) => (
          <button key={k} onClick={() => { setFiltro(k); setPag(1); }} data-testid={`filtro-${k}`}
            className={`px-3 py-1.5 text-xs rounded-md border ${filtro === k ? "border-agro-green text-agro-green bg-agro-green/10" : "border-border text-text-secondary"} ${k === "atencion" && atencion.length ? "text-agro-yellow" : ""}`}>{l}</button>
        ))}
      </div>

      <div className="bg-card border border-border/60 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
            <tr className="text-left">
              <th className="p-3 pl-4 w-10">
                {puedeEditar && (
                  <button onClick={() => marcarVisibles(!todosVisiblesMarcados)} title={`Marcar / desmarcar los ${visibles.length} productos filtrados en ${activo.toUpperCase()}`}
                    data-testid="marcar-visibles" className="text-text-secondary hover:text-agro-green">
                    {todosVisiblesMarcados ? <CheckSquareIcon size={16} /> : <SquareIcon size={16} />}
                  </button>
                )}
              </th>
              <th className="p-3">Código</th><th className="p-3">Producto</th>
              <th className="p-3 text-right">Precio {activo.toUpperCase()}</th>
              <th className="p-3">Asignación</th>
              <th className="p-3 text-right pr-4">Precio aplicado</th>
            </tr>
          </thead>
          <tbody>
            {pagina.length === 0 ? (
              <tr><td colSpan="6" className="p-8 text-center text-text-muted">Sin productos para este filtro</td></tr>
            ) : pagina.map(({ p, ex, ef }) => {
              const enOtro = ex && ex !== activo;
              return (
                <tr key={p.id} className="border-t border-border/40" data-testid={`prod-${p.codigo}`}>
                  <td className="p-3 pl-4">
                    <input type="checkbox" checked={ex === activo} disabled={!puedeEditar || enOtro} onChange={() => alternar(p.id)}
                      className="accent-agro-green" data-testid={`check-${p.codigo}`}
                      title={enOtro ? `Seleccionado en ${ex.toUpperCase()}: desmárcalo allí o usa «Mover aquí»` : ""} />
                  </td>
                  <td className="p-3 font-mono text-xs text-agro-green">{p.codigo}{p.sku ? <span className="text-text-muted"> · {p.sku}</span> : ""}</td>
                  <td className="p-3 text-xs">{p.nombre || p.descripcion}</td>
                  <td className="p-3 text-right text-xs">{Number(p[`precio_${activo}`]) > 0 ? fmtL(p[`precio_${activo}`]) : <span className="text-text-muted">sin precio</span>}</td>
                  <td className="p-3 text-xs" data-testid={`asig-${p.codigo}`}>
                    {ex ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className={`px-1.5 py-0.5 rounded border font-mono font-semibold ${COLOR[ex]}`}>{ex.toUpperCase()}</span>
                        <span className="text-text-secondary">explícito</span>
                        {enOtro && puedeEditar && (
                          <button onClick={() => setAsig((a) => ({ ...a, [p.id]: activo }))} data-testid={`mover-${p.codigo}`}
                            className="ml-1 text-[11px] text-agro-green hover:underline inline-flex items-center gap-0.5">
                            <ArrowRightIcon size={10} /> Mover a {activo.toUpperCase()}
                          </button>
                        )}
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-text-muted">
                        <span className={`px-1.5 py-0.5 rounded border border-dashed font-mono ${COLOR[base] || ""}`}>{base.toUpperCase()}</span>
                        automático{resto ? " · Todos los demás" : " · nivel del cliente"}
                      </span>
                    )}
                  </td>
                  <td className="p-3 pr-4 text-right">
                    <div className="font-semibold">{fmtL(ef.precio)}</div>
                    {ef.atencion && (
                      <div className="text-[10px] text-agro-yellow inline-flex items-center gap-1" data-testid={`atencion-${p.codigo}`}>
                        <WarningIcon size={11} /> {(ex || base).toUpperCase()} sin precio{ef.precio > 0 ? ` · se usa ${ef.nivel.toUpperCase()}` : ""}
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="flex items-center justify-between px-4 py-2 border-t border-border/40 text-xs text-text-muted">
          <span>{visibles.length} producto(s) · página {Math.min(pag, paginas)} de {paginas}</span>
          <div className="flex gap-2">
            <button disabled={pag <= 1} onClick={() => setPag(pag - 1)} className="px-3 py-1 border border-border rounded disabled:opacity-40" data-testid="pag-ant">Anterior</button>
            <button disabled={pag >= paginas} onClick={() => setPag(pag + 1)} className="px-3 py-1 border border-border rounded disabled:opacity-40" data-testid="pag-sig">Siguiente</button>
          </div>
        </div>
      </div>

      {puedeEditar && (
        <div className="sticky bottom-0 bg-card/95 border border-border rounded-lg p-3 flex items-center justify-between gap-3" data-testid="barra-guardar">
          <span className="text-xs text-text-secondary">
            {cambios ? <><b className="text-agro-yellow">{cambios}</b> cambio(s) sin guardar</> : "Sin cambios pendientes"}
          </span>
          <div className="flex gap-2">
            <button disabled={!cambios || guardando} onClick={() => { setAsig(orig.asig); setResto(orig.resto); }}
              className="px-3 py-1.5 text-xs border border-border rounded-md disabled:opacity-40" data-testid="descartar">Descartar</button>
            <button disabled={!cambios || guardando} onClick={guardar} data-testid="guardar-niveles"
              className="px-4 py-1.5 text-xs bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md disabled:opacity-40">
              {guardando ? "Guardando…" : "Guardar cambios"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
